<template>
  <div class="release-wrapper">
    <v-container
      class="pa-0 pa-sm-3 release-container-fix"
      :class="{ 'edit-mode-container': mode === 'edit' }"
    >
      <!-- New Listing Section -->
      <template v-if="mode === 'new'">
        <v-row>
          <v-col cols="12">
            <v-card class="mx-auto release-header pb-6" elevation="3">
              <v-img
                src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80"
                height="120"
                gradient="to bottom, rgba(0,0,0,.4), rgba(0,0,0,.7)"
                class="white--text align-center"
              >
                <v-card-title class="d-flex align-center py-3">
                  <v-btn
                    icon
                    color="white"
                    class="mr-3"
                    @click="$router.replace('/home')"
                    elevation="2"
                  >
                    <v-icon>mdi-arrow-left</v-icon>
                  </v-btn>
                  <div class="d-flex flex-column align-start">
                    <span class="text-subtitle-2 white--text">New Listing</span>
                    <h3 class="text-h5 font-weight-bold mb-0 white--text">
                      Create New Release
                    </h3>
                  </div>
                </v-card-title>
              </v-img>
            </v-card>
          </v-col>
        </v-row>

        <!-- Stepper for new listing -->
        <v-stepper v-model="currentStep" class="elevation-0">
          <v-stepper-header class="elevation-1 rounded-lg mb-4">
            <v-stepper-step
              :complete="currentStep > 1"
              step="1"
              color="primary"
            >
              Basic Information
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step
              :complete="currentStep > 2"
              step="2"
              color="primary"
            >
              Product Details
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step
              :complete="currentStep > 3"
              step="3"
              color="primary"
            >
              Tracklist
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step
              :complete="currentStep > 4"
              step="4"
              color="primary"
            >
              Pricing
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step
              :complete="currentStep > 5"
              step="5"
              color="primary"
            >
              Inventory
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step
              :complete="currentStep > 6"
              step="6"
              color="primary"
            >
              Images
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step
              :complete="currentStep > 7"
              step="7"
              color="primary"
            >
              Description
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="8" color="primary"> Review </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- Step 1: Basic Information -->
            <v-stepper-content step="1">
              <v-card flat class="mb-4">
                <v-card-title class="subtitle-1 font-weight-bold py-2 pl-0">
                  <v-icon color="primary" class="mr-2">mdi-music-note</v-icon>
                  Basic Information
                </v-card-title>
                <v-card-text class="pa-0">
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="release.title"
                        :rules="[(v) => !!v || 'Field is required']"
                        label="Title"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4 elevation-1 rounded-lg"
                        prepend-inner-icon="mdi-tag-text-outline"
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="Enter release title"
                        counter="100"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="release.artist"
                        :rules="[(v) => !!v || 'Field is required']"
                        label="Artist"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4 elevation-1 rounded-lg"
                        prepend-inner-icon="mdi-account-music-outline"
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="Enter artist name"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="release.year"
                        :rules="[(v) => !!v || 'Field is required']"
                        label="Year"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4 elevation-1 rounded-lg"
                        prepend-inner-icon="mdi-calendar-outline"
                        color="primary"
                        background-color="grey lighten-5"
                        type="number"
                        placeholder="YYYY"
                        min="1900"
                        max="2099"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="release.label"
                        :rules="[(v) => !!v || 'Field is required']"
                        label="Label"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4 elevation-1 rounded-lg"
                        prepend-inner-icon="mdi-label-outline"
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="Enter record label"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="release.country"
                        label="Country"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4 elevation-1 rounded-lg"
                        prepend-inner-icon="mdi-flag-outline"
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="Country of origin"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="release.format"
                        label="Format"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4 elevation-1 rounded-lg"
                        prepend-inner-icon="mdi-disc"
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="LP, CD, etc."
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="$router.replace('/home')">Cancel</v-btn>
                <v-btn color="primary" @click="currentStep = 2">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 2: Product Details -->
            <v-stepper-content step="2">
              <v-card flat class="mb-4">
                <v-card-title class="subtitle-1 font-weight-bold py-2 pl-0">
                  <v-icon color="primary" class="mr-2">mdi-album</v-icon>
                  Product Details
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.productType"
                        :items="['vinyl', 'cd', 'cassette']"
                        label="Product Type"
                        @change="handleCategory"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        required
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.format"
                        :items="formatOptions"
                        label="Format"
                        @change="handleFormatChange"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        required
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.speed"
                        :items="['33', '45', '78']"
                        label="Speed"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        required
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :items="['New', 'Used']"
                        v-model="release.condition"
                        label="Condition"
                        @change="handleCategory"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        required
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.language"
                        :items="[
                          'English',
                          'Hindi',
                          'Bengali',
                          'Gujrati',
                          'Tamil',
                          'Telegu',
                          'Malayalam',
                          'Punjabi',
                          'Konkani',
                          'Spanish',
                          'Italian',
                          'French',
                          'Russian',
                          'Japanese',
                        ]"
                        label="Language"
                        @change="handleCategory"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        required
                      ></v-select>
                    </v-col>
                  </v-row>

                  <!-- Condition Section -->
                  <v-card flat outlined class="pa-4 mb-4">
                    <div class="subtitle-1 font-weight-medium mb-4">
                      <v-icon small color="primary" class="mr-1"
                        >mdi-check-circle-outline</v-icon
                      >
                      Condition
                    </div>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="release.coverCondition"
                          :items="conditionOptions"
                          item-title="text"
                          item-value="value"
                          label="Cover Condition"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-4"
                          required
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="release.recordCondition"
                          :items="conditionOptions"
                          item-title="text"
                          item-value="value"
                          label="Record Condition"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-4"
                          required
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-card>

                  <!-- Special Categories -->
                  <v-card flat outlined class="pa-4 mb-4">
                    <div class="subtitle-1 font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1"
                        >mdi-shape-outline</v-icon
                      >
                      Special Categories
                    </div>
                    <v-checkbox
                      v-model="release.customCategory"
                      label="Featured"
                      value="Featured"
                      hide-details
                      class="mt-0 pt-0"
                      dense
                    ></v-checkbox>
                    <v-checkbox
                      v-model="release.customCategory"
                      label="Import"
                      value="Import"
                      hide-details
                      class="mt-0 pt-0"
                      dense
                    ></v-checkbox>
                    <v-checkbox
                      v-model="release.customCategory"
                      label="Japanese Pressings"
                      value="Japanese Pressings"
                      hide-details
                      class="mt-0 pt-0"
                      dense
                    ></v-checkbox>
                    <v-checkbox
                      v-model="release.customCategory"
                      label="DJ Records"
                      value="DJ Records"
                      hide-details
                      class="mt-0 pt-0"
                      dense
                    ></v-checkbox>
                  </v-card>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="currentStep = 1">Back</v-btn>
                <v-btn color="primary" @click="currentStep = 3">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 3: Tracklist -->
            <v-stepper-content step="3">
              <v-card flat class="mb-4">
                <v-card-title class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2"
                      >mdi-playlist-music</v-icon
                    >
                    <span class="font-weight-bold">Tracklist</span>
                  </div>
                  <v-btn
                    color="primary"
                    small
                    outlined
                    class="px-3"
                    @click="addTrack"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Add Track
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="headers"
                    :items="release.tracklist"
                    :items-per-page="15"
                    class="elevation-0"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 15, 20, 25],
                    }"
                  >
                    <template v-slot:item.position="{ item }">
                      <v-edit-dialog
                        :return-value.sync="item.position"
                        large
                        persistent
                        transition="fade-transition"
                      >
                        <div class="font-weight-medium">
                          {{ item.position }}
                        </div>
                        <template v-slot:input>
                          <div class="mt-4 subtitle-1 font-weight-medium">
                            Edit Position
                          </div>
                          <v-text-field
                            v-model="item.position"
                            label="Track Position"
                            outlined
                            dense
                            autofocus
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>

                    <template v-slot:item.title="{ item }">
                      <v-edit-dialog
                        :return-value.sync="item.title"
                        large
                        persistent
                        transition="fade-transition"
                      >
                        <div>{{ item.title }}</div>
                        <template v-slot:input>
                          <div class="mt-4 subtitle-1 font-weight-medium">
                            Edit Title
                          </div>
                          <v-text-field
                            v-model="item.title"
                            label="Track Title"
                            outlined
                            dense
                            autofocus
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>

                    <template v-slot:item.duration="{ item }">
                      <v-edit-dialog
                        :return-value.sync="item.duration"
                        large
                        persistent
                        transition="fade-transition"
                      >
                        <div>{{ item.duration }}</div>
                        <template v-slot:input>
                          <div class="mt-4 subtitle-1 font-weight-medium">
                            Edit Duration
                          </div>
                          <v-text-field
                            v-model="item.duration"
                            label="Track Duration"
                            outlined
                            dense
                            autofocus
                            placeholder="mm:ss"
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        @click="removeTrack(item)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="currentStep = 2">Back</v-btn>
                <v-btn color="primary" @click="currentStep = 4">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 4: Pricing -->
            <v-stepper-content step="4">
              <v-card flat class="mb-4">
                <v-card-title class="subtitle-1 font-weight-bold py-2 pl-0">
                  <v-icon color="primary" class="mr-2">mdi-currency-usd</v-icon>
                  Pricing
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        v-model="release.listPrice"
                        label="List Price"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prefix="₹"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        v-model="release.sellingPrice"
                        label="Selling Price"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prefix="₹"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        v-model="release.buyingPrice"
                        label="Buying Price"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prefix="₹"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="purchase"
                        :items="purchases"
                        item-text="purchaseId"
                        item-value="_id"
                        label="Purchase ID"
                        return-object
                        @change="handlePurchaseChange"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <!-- Discogs Price Suggestions Section -->
                  <v-card
                    outlined
                    class="mt-4 mb-4"
                    v-if="release.lowest_price"
                  >
                    <v-card-title class="subtitle-1">
                      <v-icon color="primary" left>mdi-tag-multiple</v-icon>
                      Discogs Price Suggestions
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-card flat class="text-center pa-2" outlined>
                            <div
                              class="subtitle-2 grey--text text--darken-1 mb-1"
                            >
                              Lowest Price
                            </div>
                            <div
                              class="headline primary--text font-weight-medium"
                            >
                              ${{ release.lowest_price || "0.00" }}
                            </div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" md="4" v-if="release.median_price">
                          <v-card flat class="text-center pa-2" outlined>
                            <div
                              class="subtitle-2 grey--text text--darken-1 mb-1"
                            >
                              Median Price
                            </div>
                            <div
                              class="headline primary--text font-weight-medium"
                            >
                              ${{ release.median_price || "0.00" }}
                            </div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" md="4" v-if="release.highest_price">
                          <v-card flat class="text-center pa-2" outlined>
                            <div
                              class="subtitle-2 grey--text text--darken-1 mb-1"
                            >
                              Highest Price
                            </div>
                            <div
                              class="headline primary--text font-weight-medium"
                            >
                              ${{ release.highest_price || "0.00" }}
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>
                      <div
                        class="mt-2 caption grey--text text--darken-1 text-center"
                      >
                        Price suggestions from Discogs marketplace data.
                        Consider these when setting your prices.
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- Similar Products Section -->
                  <v-card class="mt-4" v-if="similarProducts.length">
                    <v-card-title>Other Products with Same Title</v-card-title>
                    <v-card-text>
                      <div style="display: flex; overflow-x: auto; gap: 24px">
                        <div
                          v-for="product in similarProducts"
                          :key="product.sku"
                          style="
                            min-width: 200px;
                            max-width: 200px;
                            flex: 0 0 auto;
                          "
                        >
                          <v-card
                            outlined
                            class="pa-2"
                            style="
                              height: 100%;
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                              border-radius: 12px;
                              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
                              background: #fafbfc;
                              transition: box-shadow 0.2s;
                            "
                          >
                            <v-img
                              v-if="product.images && product.images.length"
                              :src="getImageUrl(product.images[0])"
                              height="120"
                              min-height="120"
                              contain
                              class="mb-2"
                              style="border-radius: 8px"
                            />
                            <v-img
                              v-else
                              src="https://via.placeholder.com/200x120?text=No+Image"
                              height="120"
                              min-height="120"
                              contain
                              class="mb-2"
                              style="border-radius: 8px"
                            />
                            <div
                              class="font-weight-bold mb-2"
                              style="
                                font-size: 1.05rem;
                                text-align: center;
                                word-break: break-word;
                              "
                            >
                              {{ product.title }}
                            </div>
                            <div
                              class="mb-2"
                              style="
                                display: flex;
                                flex-wrap: wrap;
                                justify-content: center;
                                gap: 4px;
                              "
                            >
                              <v-chip
                                small
                                color="primary"
                                text-color="white"
                                v-if="product.condition"
                              >
                                {{ product.condition }}
                              </v-chip>
                              <v-chip
                                small
                                color="teal"
                                text-color="white"
                                v-if="product.coverCondition"
                              >
                                Cover: {{ product.coverCondition }}
                              </v-chip>
                              <v-chip
                                small
                                color="deep-purple"
                                text-color="white"
                                v-if="product.recordCondition"
                              >
                                Record: {{ product.recordCondition }}
                              </v-chip>
                            </div>
                            <div
                              style="
                                width: 100%;
                                text-align: center;
                                margin-top: 8px;
                              "
                            >
                              <div style="font-weight: 500">
                                <span style="color: #333">Sale Price:</span> ₹{{
                                  product.sellingPrice
                                }}
                              </div>
                              <div style="font-weight: 500">
                                <span style="color: #333">Buy Price:</span> ₹{{
                                  product.buyingPrice
                                }}
                              </div>
                            </div>
                          </v-card>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- No Similar Products Message -->
                  <v-card class="mt-4" v-else>
                    <v-card-text class="text-center pa-4">
                      <v-icon color="grey" x-large class="mb-2"
                        >mdi-database-search</v-icon
                      >
                      <div class="subtitle-1 grey--text text--darken-1">
                        No similar products found
                      </div>
                      <div class="caption grey--text mt-1">
                        This appears to be a unique item in your inventory.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="currentStep = 3">Back</v-btn>
                <v-btn color="primary" @click="currentStep = 5">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 5: Inventory -->
            <v-stepper-content step="5">
              <v-card flat class="mb-4">
                <v-card-title class="subtitle-1 font-weight-bold py-2 pl-0">
                  <v-icon color="primary" class="mr-2"
                    >mdi-package-variant</v-icon
                  >
                  Inventory
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        v-model="release.noofrec"
                        label="Number of Records"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-format-list-numbered"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        v-model="release.weight"
                        label="Weight"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-weight"
                        suffix="g"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        v-model="release.inventoryLocation"
                        label="Inventory Location"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-map-marker"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.wcCategory"
                        :items="categoryOptions"
                        label="Category"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        required
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-card outlined class="pa-3 mb-4">
                    <div class="subtitle-2 mb-2 font-weight-medium">
                      Dimensions (cm)
                    </div>
                    <v-row dense>
                      <v-col cols="4">
                        <v-text-field
                          :rules="[(v) => !!v || 'Required']"
                          label="Length"
                          v-model="release.dimensions.length"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-2"
                          type="number"
                          suffix="cm"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          :rules="[(v) => !!v || 'Required']"
                          label="Width"
                          v-model="release.dimensions.width"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-2"
                          type="number"
                          suffix="cm"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          :rules="[(v) => !!v || 'Required']"
                          label="Height"
                          v-model="release.dimensions.height"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-2"
                          type="number"
                          suffix="cm"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="currentStep = 4">Back</v-btn>
                <v-btn color="primary" @click="currentStep = 6">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 6: Images -->
            <v-stepper-content step="6">
              <v-card flat class="mb-4">
                <v-card-title class="d-flex align-center">
                  <v-icon color="primary" class="mr-2"
                    >mdi-image-multiple</v-icon
                  >
                  <span class="font-weight-bold">Product Images</span>
                </v-card-title>
                <v-card-text>
                  <v-file-input
                    label="Upload or take pictures"
                    accept="image/*"
                    @change="onFileChange"
                    :disabled="mode == 'edit'"
                    multiple
                    chips
                    show-size
                    counter
                    prepend-icon="mdi-camera"
                    outlined
                    dense
                    placeholder="Select images or take photos"
                    hint="Add multiple product images for better visibility"
                    persistent-hint
                  ></v-file-input>
                </v-card-text>
              </v-card>

              <!-- Image Gallery -->
              <v-card
                v-if="
                  images.length || (mode == 'edit' && release.images.length)
                "
                elevation="2"
                class="mt-4"
              >
                <v-card-title class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2"
                      >mdi-image-album</v-icon
                    >
                    <span class="font-weight-bold">Image Gallery</span>
                  </div>
                  <div>
                    <v-chip color="primary" outlined small class="mr-2">
                      <v-icon left x-small>mdi-information-outline</v-icon>
                      First image will be the product thumbnail
                    </v-chip>
                  </div>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <!-- New uploaded images -->
                    <v-col
                      v-for="(image, i) in imagesWithKeys"
                      :key="image.id"
                      cols="12"
                      xs="6"
                      sm="4"
                      md="3"
                      class="image-gallery-item"
                    >
                      <v-hover v-slot="{ hover }">
                        <v-card
                          class="mx-auto image-card"
                          :elevation="hover ? 8 : 2"
                        >
                          <div class="image-order-badge primary">
                            {{ i + 1 }}
                          </div>
                          <v-img
                            :src="image.src"
                            aspect-ratio="1"
                            contain
                            height="150"
                            class="grey lighten-4"
                          >
                            <template v-slot:placeholder>
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <v-progress-circular
                                  indeterminate
                                  color="primary"
                                ></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                          <v-card-actions
                            class="pa-2 justify-center image-card-actions"
                          >
                            <v-btn
                              icon
                              x-small
                              color="primary"
                              @click="moveImageUp(i)"
                              :disabled="i === 0"
                            >
                              <v-icon x-small>mdi-arrow-up</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              x-small
                              color="primary"
                              @click="moveImageDown(i)"
                              :disabled="i === images.length - 1"
                            >
                              <v-icon x-small>mdi-arrow-down</v-icon>
                            </v-btn>
                            <v-btn
                              x-small
                              color="error"
                              outlined
                              @click="removeImage(i)"
                              class="ml-1"
                            >
                              <v-icon x-small left>mdi-delete</v-icon>
                              Remove
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-hover>
                    </v-col>

                    <!-- Existing images (edit mode) -->
                    <v-col
                      v-if="mode == 'edit'"
                      v-for="(image, index) in existingImagesWithKeys"
                      :key="image.id"
                      cols="12"
                      xs="6"
                      sm="4"
                      md="3"
                      class="image-gallery-item"
                    >
                      <v-hover v-slot="{ hover }">
                        <v-card
                          class="mx-auto image-card"
                          :elevation="hover ? 8 : 2"
                        >
                          <div class="image-order-badge primary">
                            {{ index + 1 }}
                          </div>
                          <v-img
                            :src="getImageUrl(image.src)"
                            class="grey lighten-4"
                          >
                            <template v-slot:placeholder>
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <v-progress-circular
                                  indeterminate
                                  color="primary"
                                ></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                          <v-card-actions
                            class="pa-2 justify-center image-card-actions"
                          >
                            <v-btn
                              icon
                              x-small
                              color="primary"
                              @click="moveExistingImageUp(index)"
                              :disabled="index === 0"
                            >
                              <v-icon x-small>mdi-arrow-up</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              x-small
                              color="primary"
                              @click="moveExistingImageDown(index)"
                              :disabled="index === release.images.length - 1"
                            >
                              <v-icon x-small>mdi-arrow-down</v-icon>
                            </v-btn>
                            <v-btn
                              x-small
                              color="error"
                              outlined
                              @click="removeImageForRelease(index)"
                              class="ml-1"
                            >
                              <v-icon x-small left>mdi-delete</v-icon>
                              Remove
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-hover>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <div class="d-flex justify-space-between mt-4">
                <v-btn text @click="currentStep = 5">Back</v-btn>
                <v-btn color="primary" @click="currentStep = 7">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 7: Description -->
            <v-stepper-content step="7">
              <v-card flat class="mb-4">
                <v-card-title class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2"
                      >mdi-text-box-outline</v-icon
                    >
                    <span class="font-weight-bold">Product Descriptions</span>
                  </div>
                  <v-btn color="secondary" outlined @click="generateGPT">
                    <v-icon left>mdi-robot</v-icon>
                    Generate Description
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        label="Product Name"
                        v-model="release.title"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-tag-text"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        label="Full Description"
                        v-model="release.description"
                        outlined
                        hide-details="auto"
                        class="mb-4"
                        rows="5"
                        counter
                        prepend-inner-icon="mdi-text-box-outline"
                        hint="Detailed product description for the listing page"
                        persistent-hint
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        :rules="[(v) => !!v || 'Field is required']"
                        label="Short Description"
                        v-model="release.short_description"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-text-short"
                        hint="Brief summary shown in product listings"
                        persistent-hint
                        counter
                        maxlength="150"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.genre"
                        :items="defaultGenre"
                        label="Genre"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-music"
                        :rules="[(v) => !!v || 'Field is required']"
                        required
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="Select music genre"
                        menu-props="{ maxHeight: '400px' }"
                        hint="Select the primary genre of the release"
                        persistent-hint
                      >
                        <template v-slot:selection="{ item }">
                          <v-chip small color="primary" outlined class="mr-1">
                            <v-icon left x-small>mdi-music-note</v-icon>
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="release.mood"
                        :items="moodOptions"
                        label="Mood"
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-emoticon-outline"
                        :rules="[(v) => !!v || 'Field is required']"
                        required
                        color="primary"
                        background-color="grey lighten-5"
                        placeholder="Select mood"
                        menu-props="{ maxHeight: '400px' }"
                        hint="Select the mood that best describes this release"
                        persistent-hint
                      >
                        <template v-slot:selection="{ item }">
                          <v-chip small color="secondary" outlined class="mr-1">
                            <v-icon left x-small>mdi-emoticon-outline</v-icon>
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>

                  <v-divider class="my-4"></v-divider>

                  <!-- Keywords section -->
                  <div class="subtitle-1 font-weight-medium mb-3">
                    <v-icon small color="primary" class="mr-1"
                      >mdi-tag-multiple</v-icon
                    >
                    Keywords
                  </div>
                  <v-row>
                    <v-col cols="12">
                      <v-combobox
                        v-model="release.keywords"
                        :items="release.keywords"
                        hide-selected
                        label="Keywords"
                        multiple
                        small-chips
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                        prepend-inner-icon="mdi-tag-multiple-outline"
                        hint="Add keywords to improve search visibility"
                        persistent-hint
                      >
                        <template v-slot:no-data>
                          <v-list-item @click="addKeyword(search)">
                            <v-list-item-content>
                              <v-list-item-title>
                                Add <strong>{{ search }}</strong>
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                        <template
                          v-slot:selection="{ attrs, item, parent, selected }"
                        >
                          <v-chip
                            v-bind="attrs"
                            :color="`${item.color || 'primary'} lighten-3`"
                            :input-value="selected"
                            label
                            small
                            class="ma-1"
                          >
                            <span class="pr-2">{{ item }}</span>
                            <v-icon x-small @click="parent.selectItem(item)"
                              >mdi-close</v-icon
                            >
                          </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-chip
                            :color="`${item.color || 'primary'} lighten-3`"
                            dark
                            label
                            small
                          >
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="currentStep = 6">Back</v-btn>
                <v-btn color="primary" @click="currentStep = 8">Continue</v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 8: Review -->
            <v-stepper-content step="8">
              <v-card flat class="mb-4">
                <v-card-title class="subtitle-1 font-weight-bold py-2 pl-0">
                  <v-icon color="primary" class="mr-2"
                    >mdi-check-circle-outline</v-icon
                  >
                  Review Your Listing
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="4">
                      <!-- Basic details -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-information-outline</v-icon
                          >
                          Basic Information
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Title</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.title
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Artist</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.artist
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Year</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.year
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Label</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.label
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Country</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.country
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Catalog No.</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">
                              {{ release.catlogNo || "Not specified" }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Product details -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-album</v-icon
                          >
                          Product Details
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Product Type</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.productType
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Format</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.format
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Speed</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.speed
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Condition</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.condition
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Language</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.language
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Number of Records</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.noofrec
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Pricing -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-currency-usd</v-icon
                          >
                          Pricing
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >List Price</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >₹{{ release.listPrice }}</v-list-item-title
                            >
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Selling Price</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >₹{{ release.sellingPrice }}</v-list-item-title
                            >
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Buying Price</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >₹{{ release.buyingPrice }}</v-list-item-title
                            >
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item
                          dense
                          class="px-0 py-1"
                          v-if="purchase && purchase.purchaseId"
                        >
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Purchase ID</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              purchase.purchaseId
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Condition -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-check-circle-outline</v-icon
                          >
                          Condition Details
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Cover Condition</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.coverCondition
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Record Condition</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.recordCondition
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Inventory -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-package-variant</v-icon
                          >
                          Inventory
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Quantity</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.noofrec
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Weight</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >{{ release.weight }}g</v-list-item-title
                            >
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Location</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.inventoryLocation
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Category</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.wcCategory
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Dimensions -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-ruler-square</v-icon
                          >
                          Dimensions
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Length</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >{{ release.dimensions.length }} cm
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Width</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >{{ release.dimensions.width }}
                              cm
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Height</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2"
                              >{{ release.dimensions.height }} cm
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Content Details -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-text-box-outline</v-icon
                          >
                          Content Details
                        </div>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Genre</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.genre
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Mood</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2">{{
                              release.mood
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item dense class="px-0 py-1">
                          <v-list-item-content>
                            <v-list-item-subtitle class="caption"
                              >Short Description</v-list-item-subtitle
                            >
                            <v-list-item-title class="body-2 text-truncate">{{
                              release.short_description
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Special Categories -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-shape-outline</v-icon
                          >
                          Special Categories
                        </div>
                        <v-chip-group column>
                          <v-chip
                            v-for="(category, i) in release.customCategory"
                            :key="i"
                            small
                            outlined
                            color="primary"
                          >
                            {{ category }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            color="grey"
                            v-if="
                              !release.customCategory ||
                              release.customCategory.length === 0
                            "
                          >
                            None
                          </v-chip>
                        </v-chip-group>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                      <!-- Keywords -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-tag-multiple</v-icon
                          >
                          Keywords
                        </div>
                        <v-chip-group column>
                          <v-chip
                            v-for="(keyword, i) in release.keywords"
                            :key="i"
                            small
                            outlined
                            color="secondary"
                          >
                            {{ keyword }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            color="grey"
                            v-if="
                              !release.keywords || release.keywords.length === 0
                            "
                          >
                            No Keywords
                          </v-chip>
                        </v-chip-group>
                      </v-card>
                    </v-col>

                    <v-col cols="12">
                      <!-- Tracklist -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-playlist-music</v-icon
                          >
                          Tracklist
                        </div>
                        <v-simple-table
                          dense
                          v-if="
                            release.tracklist && release.tracklist.length > 0
                          "
                        >
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th>Position</th>
                                <th>Title</th>
                                <th>Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="(track, index) in release.tracklist"
                                :key="index"
                              >
                                <td>{{ track.position }}</td>
                                <td>{{ track.title }}</td>
                                <td>{{ track.duration }}</td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                        <div v-else class="body-2 text-center py-2 grey--text">
                          No tracks added
                        </div>
                      </v-card>
                    </v-col>

                    <v-col cols="12" v-if="release.description">
                      <!-- Full Description -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-text-box-multiple</v-icon
                          >
                          Full Description
                        </div>
                        <div class="body-2 text-wrap">
                          {{ release.description }}
                        </div>
                      </v-card>
                    </v-col>

                    <v-col
                      cols="12"
                      v-if="release.images && release.images.length > 0"
                    >
                      <!-- Product Images -->
                      <v-card outlined class="pa-3 mb-3">
                        <div class="subtitle-2 font-weight-medium mb-2">
                          <v-icon small color="primary" class="mr-1"
                            >mdi-image-multiple</v-icon
                          >
                          Product Images
                        </div>
                        <v-row>
                          <v-col
                            v-for="(image, index) in release.images"
                            :key="index"
                            cols="6"
                            sm="4"
                            md="3"
                            lg="2"
                          >
                            <v-img
                              :src="getImageUrl(image)"
                              aspect-ratio="1"
                              contain
                              class="grey lighten-4 rounded-lg"
                              height="100"
                            >
                              <template v-slot:placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="primary"
                                  ></v-progress-circular>
                                </v-row>
                              </template>
                            </v-img>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <div class="d-flex justify-space-between">
                <v-btn text @click="currentStep = 7">Back</v-btn>
                <div>
                  <v-btn color="success" @click="save" class="ml-2">
                    <v-icon left>mdi-content-save</v-icon>
                    Save & Create SKU
                  </v-btn>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </template>

      <!-- Edit/Publish Section - use the template provided -->
      <template v-else>
        <!-- START OF EDIT MODE TEMPLATE -->
        <v-row v-show="true" style="position: relative; z-index: 1">
          <v-col cols="12">
            <v-card class="mx-auto release-header pb-6" elevation="3">
              <v-img
                src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80"
                height="120"
                gradient="to bottom, rgba(0,0,0,.4), rgba(0,0,0,.7)"
                class="white--text align-center"
              >
                <v-card-title class="d-flex align-center py-3">
                  <v-btn
                    icon
                    color="white"
                    class="mr-3"
                    @click="$router.replace('/home')"
                    elevation="2"
                  >
                    <v-icon>mdi-arrow-left</v-icon>
                  </v-btn>
                  <div class="d-flex flex-column align-start">
                    <span class="text-subtitle-2 white--text">{{
                      release.artist
                    }}</span>
                    <h3 class="text-h5 font-weight-bold mb-0 white--text">
                      {{ release.title }}
                    </h3>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip
                    :color="getStatusColor(release)"
                    small
                    class="text-capitalize"
                  >
                    <v-icon left x-small>{{ getStatusIcon(release) }}</v-icon>
                    {{ getStatusText(release) }}
                  </v-chip>
                </v-card-title>
              </v-img>
              <v-card-text class="pb-0">
                <v-row class="mx-0">
                  <v-col cols="12" sm="6" md="4" lg="3">
                    <v-card class="mx-auto overflow-hidden" elevation="3">
                      <v-img
                        :src="release.discogsuri"
                        class="album-cover"
                        height="300"
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                              color="primary"
                            ></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>

                      <v-card-text class="text-center pb-0">
                        <div class="d-flex justify-center flex-wrap">
                          <v-chip small class="ma-1" color="primary" outlined>
                            <v-icon left x-small>mdi-calendar</v-icon>
                            {{ release.year || "N/A" }}
                          </v-chip>
                          <v-chip small class="ma-1" color="secondary" outlined>
                            <v-icon left x-small>mdi-disc</v-icon>
                            {{ release.format || "N/A" }}
                          </v-chip>
                          <v-chip
                            small
                            class="ma-1"
                            color="success"
                            outlined
                            v-if="release.genre"
                          >
                            <v-icon left x-small>mdi-music</v-icon>
                            {{ release.genre }}
                          </v-chip>
                        </div>
                      </v-card-text>
                    </v-card>

                    <v-card outlined class="mt-4" elevation="2">
                      <v-card-title class="subtitle-1 font-weight-medium py-2">
                        <v-icon color="primary" class="mr-2"
                          >mdi-chart-line</v-icon
                        >
                        Market Information
                      </v-card-title>
                      <v-divider></v-divider>
                      <v-card-text>
                        <v-list-item dense class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1"
                              >mdi-account-group</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              >Have / Want</v-list-item-subtitle
                            >
                            <v-list-item-title class="font-weight-medium">
                              {{ release.have || 0 }} / {{ release.want || 0 }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item dense class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="green darken-1"
                              >mdi-currency-usd</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              >Lowest Price</v-list-item-subtitle
                            >
                            <v-list-item-title
                              class="font-weight-medium primary--text"
                            >
                              ${{ release.lowest_price || "0.00" }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="8" lg="9">
                    <v-card flat class="mb-4">
                      <v-card-title
                        class="subtitle-1 font-weight-bold py-2 pl-0"
                      >
                        <v-icon color="primary" class="mr-2"
                          >mdi-music-note</v-icon
                        >
                        Release Details
                      </v-card-title>
                      <v-card-text class="pa-0">
                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="release.title"
                              :rules="[(v) => !!v || 'Field is required']"
                              label="Title"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-tag-text-outline"
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="Enter release title"
                              counter="100"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="release.artist"
                              :rules="[(v) => !!v || 'Field is required']"
                              label="Artist"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-account-music-outline"
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="Enter artist name"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="release.year"
                              :rules="[(v) => !!v || 'Field is required']"
                              label="Year"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-calendar-outline"
                              color="primary"
                              background-color="grey lighten-5"
                              type="number"
                              placeholder="YYYY"
                              min="1900"
                              max="2099"
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="release.label"
                              :rules="[(v) => !!v || 'Field is required']"
                              label="Label"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-label-outline"
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="Enter record label"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="release.country"
                              label="Country"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-flag-outline"
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="Country of origin"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="release.format"
                              label="Format"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-disc"
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="LP, CD, etc."
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row class="mt-2">
                          <v-col cols="12">
                            <div
                              class="subtitle-2 font-weight-medium mb-2 grey--text text--darken-2"
                            >
                              <v-icon small color="primary" class="mr-1"
                                >mdi-tag-multiple</v-icon
                              >
                              Classification
                            </div>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="release.genre"
                              :items="defaultGenre"
                              label="Genre"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-music"
                              @change="handleCategory"
                              :rules="[(v) => !!v || 'Field is required']"
                              required
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="Select music genre"
                              menu-props="{ maxHeight: '400px' }"
                              hint="Select the primary genre of the release"
                              persistent-hint
                            >
                              <template v-slot:selection="{ item }">
                                <v-chip
                                  small
                                  color="primary"
                                  outlined
                                  class="mr-1"
                                >
                                  <v-icon left x-small>mdi-music-note</v-icon>
                                  {{ item }}
                                </v-chip>
                              </template>
                            </v-select>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="release.mood"
                              :items="moodOptions"
                              label="Mood"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-4 elevation-1 rounded-lg"
                              prepend-inner-icon="mdi-emoticon-outline"
                              :rules="[(v) => !!v || 'Field is required']"
                              required
                              color="primary"
                              background-color="grey lighten-5"
                              placeholder="Select mood"
                              menu-props="{ maxHeight: '400px' }"
                              hint="Select the mood that best describes this release"
                              persistent-hint
                            >
                              <template v-slot:selection="{ item }">
                                <v-chip
                                  small
                                  color="secondary"
                                  outlined
                                  class="mr-1"
                                >
                                  <v-icon left x-small
                                    >mdi-emoticon-outline</v-icon
                                  >
                                  {{ item }}
                                </v-chip>
                              </template>
                            </v-select>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card elevation="2" class="mb-6">
              <v-card-title class="d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2"
                    >mdi-playlist-music</v-icon
                  >
                  <span class="font-weight-bold">Tracklist</span>
                </div>
                <v-btn
                  color="primary"
                  small
                  outlined
                  class="px-3"
                  @click="addTrack"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Add Track
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headers"
                  :items="release.tracklist"
                  :items-per-page="15"
                  class="elevation-0"
                  :footer-props="{
                    'items-per-page-options': [5, 10, 15, 20, 25],
                  }"
                >
                  <template v-slot:item.position="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.position"
                      large
                      persistent
                      transition="fade-transition"
                    >
                      <div class="font-weight-medium">{{ item.position }}</div>
                      <template v-slot:input>
                        <div class="mt-4 subtitle-1 font-weight-medium">
                          Edit Position
                        </div>
                        <v-text-field
                          v-model="item.position"
                          label="Track Position"
                          outlined
                          dense
                          autofocus
                        ></v-text-field>
                      </template>
                    </v-edit-dialog>
                  </template>

                  <template v-slot:item.title="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.title"
                      large
                      persistent
                      transition="fade-transition"
                    >
                      <div>{{ item.title }}</div>
                      <template v-slot:input>
                        <div class="mt-4 subtitle-1 font-weight-medium">
                          Edit Title
                        </div>
                        <v-text-field
                          v-model="item.title"
                          label="Track Title"
                          outlined
                          dense
                          autofocus
                        ></v-text-field>
                      </template>
                    </v-edit-dialog>
                  </template>

                  <template v-slot:item.duration="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.duration"
                      large
                      persistent
                      transition="fade-transition"
                    >
                      <div>{{ item.duration }}</div>
                      <template v-slot:input>
                        <div class="mt-4 subtitle-1 font-weight-medium">
                          Edit Duration
                        </div>
                        <v-text-field
                          v-model="item.duration"
                          label="Track Duration"
                          outlined
                          dense
                          autofocus
                          placeholder="mm:ss"
                        ></v-text-field>
                      </template>
                    </v-edit-dialog>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click="removeTrack(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <!-- END OF TRACKLIST SECTION - RENDERING ISSUE MIGHT BE HERE -->
        <!-- The rest of the form sections for edit mode would go here -->
        <!-- Similar Products Section -->
        <v-card class="my-4" v-if="similarProducts.length">
          <v-card-title>Other Products with Same Title</v-card-title>
          <v-card-text>
            <div style="display: flex; overflow-x: auto; gap: 24px">
              <div
                v-for="product in similarProducts"
                :key="product.sku"
                style="min-width: 200px; max-width: 200px; flex: 0 0 auto"
              >
                <v-card
                  outlined
                  class="pa-2"
                  style="
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
                    background: #fafbfc;
                    transition: box-shadow 0.2s;
                  "
                >
                  <v-img
                    v-if="product.images && product.images.length"
                    :src="getImageUrl(product.images[0])"
                    height="120"
                    min-height="120"
                    contain
                    class="mb-2"
                    style="border-radius: 8px"
                  />
                  <v-img
                    v-else
                    src="https://via.placeholder.com/200x120?text=No+Image"
                    height="120"
                    min-height="120"
                    contain
                    class="mb-2"
                    style="border-radius: 8px"
                  />
                  <div
                    class="font-weight-bold mb-2"
                    style="
                      font-size: 1.05rem;
                      text-align: center;
                      word-break: break-word;
                    "
                  >
                    {{ product.title }}
                  </div>
                  <div
                    class="mb-2"
                    style="
                      display: flex;
                      flex-wrap: wrap;
                      justify-content: center;
                      gap: 4px;
                    "
                  >
                    <v-chip
                      small
                      color="primary"
                      text-color="white"
                      v-if="product.condition"
                    >
                      {{ product.condition }}
                    </v-chip>
                    <v-chip
                      small
                      color="teal"
                      text-color="white"
                      v-if="product.coverCondition"
                    >
                      Cover: {{ product.coverCondition }}
                    </v-chip>
                    <v-chip
                      small
                      color="deep-purple"
                      text-color="white"
                      v-if="product.recordCondition"
                    >
                      Record: {{ product.recordCondition }}
                    </v-chip>
                  </div>
                  <div style="width: 100%; text-align: center; margin-top: 8px">
                    <div style="font-weight: 500">
                      <span style="color: #333">Sale Price:</span> ₹{{
                        product.sellingPrice
                      }}
                    </div>
                    <div style="font-weight: 500">
                      <span style="color: #333">Buy Price:</span> ₹{{
                        product.buyingPrice
                      }}
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- No Similar Products Message -->
        <v-card class="my-4" v-else>
          <v-card-text class="text-center pa-4">
            <v-icon color="grey" x-large class="mb-2"
              >mdi-database-search</v-icon
            >
            <div class="subtitle-1 grey--text text--darken-1">
              No similar products found
            </div>
            <div class="caption grey--text mt-1">
              This appears to be a unique item in your inventory.
            </div>
          </v-card-text>
        </v-card>

        <!-- Product Information Section -->
        <v-row>
          <v-col>
            <v-card class="mx-auto" elevation="2">
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-information</v-icon>
                <span class="font-weight-bold">Product Information</span>
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-col cols="12" lg="6">
                    <v-card flat outlined class="pa-4 mb-4">
                      <div class="subtitle-1 font-weight-medium mb-4">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-album</v-icon
                        >
                        Product Details
                      </div>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.productType"
                            :items="['vinyl', 'cd', 'cassette']"
                            label="Product Type"
                            @change="handleCategory"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.format"
                            :items="formatOptions"
                            label="Format"
                            @change="handleFormatChange"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.speed"
                            :items="['33', '45', '78']"
                            label="Speed"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            :items="['New', 'Used']"
                            v-model="release.condition"
                            label="Condition"
                            @change="handleCategory"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.language"
                            :items="[
                              'English',
                              'Hindi',
                              'Bengali',
                              'Gujrati',
                              'Tamil',
                              'Telegu',
                              'Malayalam',
                              'Punjabi',
                              'Konkani',
                              'Spanish',
                              'Italian',
                              'French',
                              'Russian',
                              'Japanese',
                            ]"
                            label="Language"
                            @change="handleCategory"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                      </v-row>
                    </v-card>

                    <v-card flat outlined class="pa-4 mb-4">
                      <div class="subtitle-1 font-weight-medium mb-4">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-check-circle-outline</v-icon
                        >
                        Condition
                      </div>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.coverCondition"
                            :items="conditionOptions"
                            item-title="text"
                            item-value="value"
                            label="Cover Condition"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.recordCondition"
                            :items="conditionOptions"
                            item-title="text"
                            item-value="value"
                            label="Record Condition"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>

                  <v-col cols="12" lg="6">
                    <v-card flat outlined class="pa-4 mb-4">
                      <div class="subtitle-1 font-weight-medium mb-4">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-currency-usd</v-icon
                        >
                        Pricing
                      </div>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :rules="[(v) => !!v || 'Field is required']"
                            v-model="release.listPrice"
                            label="List Price"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            prefix="₹"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :rules="[(v) => !!v || 'Field is required']"
                            v-model="release.sellingPrice"
                            label="Selling Price"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            prefix="₹"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :rules="[(v) => !!v || 'Field is required']"
                            v-model="release.buyingPrice"
                            label="Buying Price"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            prefix="₹"
                          ></v-text-field>
                        </v-col>
                        <v-col v-if="mode != 'edit'" cols="12" md="6">
                          <v-select
                            v-model="purchase"
                            :items="purchases"
                            item-text="purchaseId"
                            item-value="_id"
                            label="Purchase ID"
                            return-object
                            @change="handlePurchaseChange"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                          ></v-select>
                        </v-col>
                      </v-row>

                      <!-- Discogs Price Suggestions Section -->
                      <div v-if="release.lowest_price" class="mt-4">
                        <div class="subtitle-2 grey--text text--darken-1 mb-2">
                          <v-icon x-small color="grey darken-1" class="mr-1"
                            >mdi-tag-multiple</v-icon
                          >
                          Discogs Price Suggestions
                        </div>
                        <v-row dense>
                          <v-col cols="4" v-if="release.lowest_price">
                            <v-card flat outlined class="pa-2 text-center">
                              <div class="caption grey--text">Lowest</div>
                              <div
                                class="subtitle-2 primary--text font-weight-medium"
                              >
                                ${{ release.lowest_price }}
                              </div>
                            </v-card>
                          </v-col>
                          <v-col cols="4" v-if="release.median_price">
                            <v-card flat outlined class="pa-2 text-center">
                              <div class="caption grey--text">Median</div>
                              <div
                                class="subtitle-2 primary--text font-weight-medium"
                              >
                                ${{ release.median_price }}
                              </div>
                            </v-card>
                          </v-col>
                          <v-col cols="4" v-if="release.highest_price">
                            <v-card flat outlined class="pa-2 text-center">
                              <div class="caption grey--text">Highest</div>
                              <div
                                class="subtitle-2 primary--text font-weight-medium"
                              >
                                ${{ release.highest_price }}
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>

                    <v-card flat outlined class="pa-4 mb-4">
                      <div class="subtitle-1 font-weight-medium mb-4">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-shape-outline</v-icon
                        >
                        Categories & Inventory
                      </div>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="release.wcCategory"
                            :items="categoryOptions"
                            label="Category"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            required
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :rules="[(v) => !!v || 'Field is required']"
                            v-model="release.noofrec"
                            label="Number of Records"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                            type="number"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :rules="[(v) => !!v || 'Field is required']"
                            v-model="release.inventoryLocation"
                            label="Inventory Location"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-4"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-card flat class="pa-2">
                            <div class="caption grey--text mb-1">
                              Special Categories
                            </div>
                            <v-checkbox
                              v-model="release.customCategory"
                              label="Featured"
                              value="Featured"
                              hide-details
                              class="mt-0 pt-0"
                              dense
                            ></v-checkbox>
                            <v-checkbox
                              v-model="release.customCategory"
                              label="Import"
                              value="Import"
                              hide-details
                              class="mt-0 pt-0"
                              dense
                            ></v-checkbox>
                            <v-checkbox
                              v-model="release.customCategory"
                              label="Japanese Pressings"
                              value="Japanese Pressings"
                              hide-details
                              class="mt-0 pt-0"
                              dense
                            ></v-checkbox>
                            <v-checkbox
                              v-model="release.customCategory"
                              label="DJ Records"
                              value="DJ Records"
                              hide-details
                              class="mt-0 pt-0"
                              dense
                            ></v-checkbox>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Images Section -->
        <v-row>
          <v-col>
            <v-card class="mx-auto" elevation="2">
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-image-multiple</v-icon>
                <span class="font-weight-bold">Product Images</span>
              </v-card-title>
              <v-card-text>
                <v-file-input
                  label="Upload or take pictures"
                  accept="image/*"
                  @change="onFileChange"
                  :disabled="mode == 'edit'"
                  multiple
                  chips
                  show-size
                  counter
                  prepend-icon="mdi-camera"
                  outlined
                  dense
                  placeholder="Select images or take photos"
                  hint="Add multiple product images for better visibility"
                  persistent-hint
                ></v-file-input>
              </v-card-text>
            </v-card>

            <!-- Image Gallery -->
            <v-card
              v-if="images.length || (mode == 'edit' && release.images.length)"
              elevation="2"
              class="mt-4"
            >
              <v-card-title class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-image-album</v-icon>
                  <span class="font-weight-bold">Image Gallery</span>
                </div>
                <div>
                  <v-chip color="primary" outlined small class="mr-2">
                    <v-icon left x-small>mdi-information-outline</v-icon>
                    First image will be the product thumbnail
                  </v-chip>
                </div>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <!-- New uploaded images -->
                  <v-col
                    v-for="(image, i) in imagesWithKeys"
                    :key="image.id"
                    cols="12"
                    xs="6"
                    sm="4"
                    md="3"
                    class="image-gallery-item"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-card
                        class="mx-auto image-card"
                        :elevation="hover ? 8 : 2"
                      >
                        <div class="image-order-badge primary">
                          {{ i + 1 }}
                        </div>
                        <v-img
                          :src="image.src"
                          aspect-ratio="1"
                          contain
                          height="150"
                          class="grey lighten-4"
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-progress-circular
                                indeterminate
                                color="primary"
                              ></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                        <v-card-actions
                          class="pa-2 justify-center image-card-actions"
                        >
                          <v-btn
                            icon
                            x-small
                            color="primary"
                            @click="moveImageUp(i)"
                            :disabled="i === 0"
                          >
                            <v-icon x-small>mdi-arrow-up</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            x-small
                            color="primary"
                            @click="moveImageDown(i)"
                            :disabled="i === images.length - 1"
                          >
                            <v-icon x-small>mdi-arrow-down</v-icon>
                          </v-btn>
                          <v-btn
                            x-small
                            color="error"
                            outlined
                            @click="removeImage(i)"
                            class="ml-1"
                          >
                            <v-icon x-small left>mdi-delete</v-icon>
                            Remove
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-hover>
                  </v-col>

                  <!-- Existing images (edit mode) -->
                  <v-col
                    v-if="mode == 'edit'"
                    v-for="(image, index) in existingImagesWithKeys"
                    :key="image.id"
                    cols="12"
                    xs="6"
                    sm="4"
                    md="3"
                    class="image-gallery-item"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-card
                        class="mx-auto image-card"
                        :elevation="hover ? 8 : 2"
                      >
                        <div class="image-order-badge primary">
                          {{ index + 1 }}
                        </div>
                        <v-img
                          :src="getImageUrl(image.src)"
                          aspect-ratio="1"
                          contain
                          height="150"
                          class="grey lighten-4"
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-progress-circular
                                indeterminate
                                color="primary"
                              ></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                        <v-card-actions
                          class="pa-2 justify-center image-card-actions"
                        >
                          <v-btn
                            icon
                            x-small
                            color="primary"
                            @click="moveExistingImageUp(index)"
                            :disabled="index === 0"
                          >
                            <v-icon x-small>mdi-arrow-up</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            x-small
                            color="primary"
                            @click="moveExistingImageDown(index)"
                            :disabled="index === release.images.length - 1"
                          >
                            <v-icon x-small>mdi-arrow-down</v-icon>
                          </v-btn>
                          <v-btn
                            x-small
                            color="error"
                            outlined
                            @click="removeImageForRelease(index)"
                            class="ml-1"
                          >
                            <v-icon x-small left>mdi-delete</v-icon>
                            Remove
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-hover>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Description Section -->
        <v-row>
          <v-col>
            <v-card class="mx-auto" elevation="2">
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-text-box</v-icon>
                <span class="font-weight-bold">Product Descriptions</span>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :rules="[(v) => !!v || 'Field is required']"
                      label="Product Name"
                      v-model="release.title"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      prepend-inner-icon="mdi-tag-text"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      label="Full Description"
                      v-model="release.description"
                      outlined
                      hide-details="auto"
                      class="mb-4"
                      rows="5"
                      counter
                      prepend-inner-icon="mdi-text-box-outline"
                      hint="Detailed product description for the listing page"
                      persistent-hint
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      :rules="[(v) => !!v || 'Field is required']"
                      label="Short Description"
                      v-model="release.short_description"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      prepend-inner-icon="mdi-text-short"
                      hint="Brief summary shown in product listings"
                      persistent-hint
                      counter
                      maxlength="150"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="release.genre"
                      :items="defaultGenre"
                      label="Genre"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      prepend-inner-icon="mdi-music"
                      :rules="[(v) => !!v || 'Field is required']"
                      required
                      color="primary"
                      background-color="grey lighten-5"
                      placeholder="Select music genre"
                      menu-props="{ maxHeight: '400px' }"
                      hint="Select the primary genre of the release"
                      persistent-hint
                    >
                      <template v-slot:selection="{ item }">
                        <v-chip small color="primary" outlined class="mr-1">
                          <v-icon left x-small>mdi-music-note</v-icon>
                          {{ item }}
                        </v-chip>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="release.mood"
                      :items="moodOptions"
                      label="Mood"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      prepend-inner-icon="mdi-emoticon-outline"
                      :rules="[(v) => !!v || 'Field is required']"
                      required
                      color="primary"
                      background-color="grey lighten-5"
                      placeholder="Select mood"
                      menu-props="{ maxHeight: '400px' }"
                      hint="Select the mood that best describes this release"
                      persistent-hint
                    >
                      <template v-slot:selection="{ item }">
                        <v-chip small color="secondary" outlined class="mr-1">
                          <v-icon left x-small>mdi-emoticon-outline</v-icon>
                          {{ item }}
                        </v-chip>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <!-- Keywords section -->
                <div class="subtitle-1 font-weight-medium mb-3">
                  <v-icon small color="primary" class="mr-1"
                    >mdi-tag-multiple</v-icon
                  >
                  Keywords
                </div>
                <v-row>
                  <v-col cols="12">
                    <v-combobox
                      v-model="release.keywords"
                      :items="release.keywords"
                      hide-selected
                      label="Keywords"
                      multiple
                      small-chips
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      prepend-inner-icon="mdi-tag-multiple-outline"
                      hint="Add keywords to improve search visibility"
                      persistent-hint
                    >
                      <template v-slot:no-data>
                        <v-list-item @click="addKeyword(search)">
                          <v-list-item-content>
                            <v-list-item-title>
                              Add <strong>{{ search }}</strong>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template
                        v-slot:selection="{ attrs, item, parent, selected }"
                      >
                        <v-chip
                          v-bind="attrs"
                          :color="`${item.color || 'primary'} lighten-3`"
                          :input-value="selected"
                          label
                          small
                          class="ma-1"
                        >
                          <span class="pr-2">{{ item }}</span>
                          <v-icon x-small @click="parent.selectItem(item)"
                            >mdi-close</v-icon
                          >
                        </v-chip>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-chip
                          :color="`${item.color || 'primary'} lighten-3`"
                          dark
                          label
                          small
                        >
                          {{ item }}
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <!-- Shipping dimensions section -->
                <div class="subtitle-1 font-weight-medium mb-3">
                  <v-icon small color="primary" class="mr-1"
                    >mdi-package-variant</v-icon
                  >
                  Shipping Information
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :rules="[(v) => !!v || 'Field is required']"
                      label="Weight (g)"
                      v-model="release.weight"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      type="number"
                      suffix="g"
                      prepend-inner-icon="mdi-weight"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-3">
                      <div class="subtitle-2 mb-2 font-weight-medium">
                        Dimensions (cm)
                      </div>
                      <v-row dense>
                        <v-col cols="4">
                          <v-text-field
                            :rules="[(v) => !!v || 'Required']"
                            label="Length"
                            v-model="release.dimensions.length"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-2"
                            type="number"
                            suffix="cm"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            :rules="[(v) => !!v || 'Required']"
                            label="Width"
                            v-model="release.dimensions.width"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-2"
                            type="number"
                            suffix="cm"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            :rules="[(v) => !!v || 'Required']"
                            label="Height"
                            v-model="release.dimensions.height"
                            outlined
                            dense
                            hide-details="auto"
                            class="mb-2"
                            type="number"
                            suffix="cm"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row v-if="release.attributes && release.attributes.length">
                  <v-col
                    cols="12"
                    md="6"
                    v-for="(attribute, index) in release.attributes"
                    :key="index"
                  >
                    <v-select
                      :label="attribute.name"
                      v-model="attribute.options"
                      multiple
                      :items="attribute.options"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-4"
                      chips
                      small-chips
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn
                  color="error"
                  outlined
                  class="mr-3"
                  @click="deleteListing"
                >
                  <v-icon left>mdi-delete</v-icon>
                  Delete
                </v-btn>
                <v-btn
                  color="primary"
                  outlined
                  class="mr-3"
                  @click="updateListing"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Update
                </v-btn>
                <v-btn
                  v-if="!release.published"
                  color="success"
                  @click="publish"
                >
                  <v-icon left>mdi-publish</v-icon>
                  Publish
                </v-btn>
              </v-card-actions>
              <v-overlay :value="overlay">
                <v-progress-circular
                  indeterminate
                  size="64"
                ></v-progress-circular>
              </v-overlay>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-container>

    <!-- Image Cropping Dialog -->
    <v-dialog v-model="cropDialog" max-width="450px" style="background: white">
      <v-card>
        <v-card-title>{{ cropDialogTitle }}</v-card-title>
        <v-card-text class="pa-2 overflow">
          <Cropper
            ref="cropper"
            v-if="cropDialog"
            :src="imageDataUrl"
            :stencil-props="{
              aspectRatio: null,
            }"
            :resize-image="{
              touch: true,
              wheel: true,
            }"
            :zoom-on-touch="true"
            :class="{ 'mobile-cropper': $vuetify.breakpoint.smAndDown }"
            image-restriction="stencil"
          />
        </v-card-text>
        <v-card-actions class="d-flex justify-space-around flex-wrap">
          <v-btn @click="cancelCrop" class="ma-1">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn @click="rotateImage" class="ma-1">
            <v-icon left>mdi-rotate-right</v-icon>
            Rotate
          </v-btn>
          <v-btn color="primary" @click="cropImage" class="ma-1">
            <v-icon left>mdi-check</v-icon>
            Crop
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Success/Error Notification Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      top
      right
      elevation="6"
    >
      <div class="d-flex align-center">
        <v-icon v-if="snackbarColor === 'success'" left dark
          >mdi-check-circle</v-icon
        >
        <v-icon v-else-if="snackbarColor === 'error'" left dark
          >mdi-alert-circle</v-icon
        >
        <span>{{ snackbarText }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import axios from "axios";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
// import finalrelease from "../components/FinalRelease.vue"
export default {
  components: {
    // finalrelease,
    Cropper,
  },
  computed: {
    mode() {
      return this.$route.params.mode || this.$route.query.mode;
    },
    id() {
      return this.$route.params.id;
    },
    cropDialogTitle() {
      if (!this.images || !this.pendingImages) return "Crop Image";
      const currentImage = this.images.length + 1;
      const totalImages = this.images.length + this.pendingImages.length;
      return `Crop Image ${currentImage} of ${totalImages}`;
    },
    // Add computed property to give each image a unique key
    imagesWithKeys() {
      console.log("Computing imagesWithKeys, length:", this.images.length);
      return this.images.map((img, i) => {
        return {
          id: `img-${i}`,
          src: img,
          position: i,
        };
      });
    },
    // Add computed property for existing images
    existingImagesWithKeys() {
      console.log(
        "Computing existingImagesWithKeys, length:",
        this.release.images?.length || 0,
      );
      return this.release.images
        ? this.release.images.map((img, i) => {
            return {
              id: `existing-${i}`,
              src: img,
              position: i,
            };
          })
        : [];
    },
  },
  data() {
    return {
      currentStep: 1,
      ENDPOINT: process.env.VUE_APP_ENDPOINT,
      image: null,
      imageDataUrl: null,
      cropDialog: false,
      images: [],
      defaultGenre: [
        "Bengali",
        "Bhajans",
        "Ghazals",
        "Instrumentals",
        "Musical",
        "Blues",
        "Bollywood",
        "Children",
        "Devotional",
        "Electronic",
        "Folk",
        "Fusion",
        "Hollywood",
        "Indian Classical",
        "Indian Regional",
        "Jazz",
        "Pop",
        "Rabindra Sangeet",
        "Reggae",
        "Rock",
        "Western Classical",
      ],
      moodOptions: [
        "Chill",
        "Dance",
        "Energy",
        "Epic",
        "Experimental",
        "Heartfelt",
        "Laid Back",
        "Motivational",
        "Nostalgia",
        "Psychedelic",
        "Romantic",
        "Spiritual",
      ],
      headers: [
        { text: "Position", value: "position", sortable: false },
        { text: "Title", value: "title", sortable: false },
        { text: "Duration", value: "duration", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      overlay: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      snackbarTimeout: 4000,
      release: {
        title: "",
        description: "",
        format: "LP",
        noofrec: 1,
        short_description: "",
        year: "",
        weight: "500",
        dimensions: {
          length: 35,
          width: 35,
          height: 5,
        },
        listPrice: 1499,
        speed: "33",
        sellingPrice: 1499,
        album: "",
        artist: "",
        genre: "",
        tracklist: [],
        language: "English",
        label: "",
        catlogNo: "",
        recordCondition: "M",
        coverCondition: "M",
        condition: "Used",
        images: [],
        wcCategory: "",
        buyingPrice: 500,
        inventoryLocation: "A",
        productType: "",
        country: "",
        customCategory: [],
        console: "",
        model: "",
        manufacturer: "",
        mechanism: "",
        region: "",
        keywords: [],
        mood: "",
      },
      productArray: ["vinyl", "cd", "cassette"],
      responseData: false,
      conOptions: ["New", "Preowned"],
      conditionOptions: [
        {
          text: "Mint (M)",
          value: "M",
        },
        {
          text: "Near Mint (NM)",
          value: "NM",
        },
        {
          text: "Very Good Plus (VG+)",
          value: "VG+",
        },
        {
          text: "Very Good (VG)",
          value: "VG",
        },
        {
          text: "Very Good Minus (VG-)",
          value: "VG-",
        },
        {
          text: "Poor (P)",
          value: "P",
        },
        {
          text: "BSTOCK",
          value: "BSTOCK",
        },
        {
          text: "Remade",
          value: "Remade",
        },
      ],
      categoryOptions: [
        "Preowned Hindi Vinyl",
        "Preowned English Vinyl",
        "Preowned Bengali Vinyl",
        "Preowned Others Vinyl",
        "Preowned 78 RPM Vinyl",
        "Preowned Hindi CD",
        "Preowned English CD",
        "Preowned Bengali CD",
        "Preowned Others CD",
        "Preowned Hindi Cassette",
        "Preowned English Cassette",
        "Preowned Bengali Cassette",
        "Preowned Others Cassette",
        "New Hindi Vinyl",
        "New English Vinyl",
        "New Bengali Vinyl",
        "New Others Vinyl",
        "New Hindi CD",
        "New English CD",
        "New Bengali CD",
        "New Others CD",
        "New Hindi Cassette",
        "New English Cassette",
        "New Bengali Cassette",
        "New Others Cassette",
      ],
      formatOptions: [
        "LP",
        "EP",
        "SP",
        "CD",
        "SACD",
        "SHMCD",
        "DVDAUDIO",
        "CASSETTE",
      ],
      search: "",
      colors: ["green", "purple", "indigo", "cyan", "teal", "orange"],
      nonce: 1,
      purchase: {},
      purchases: [],
      pendingImages: [],
      isProcessingFiles: false,
      cropperKey: 0,
      similarProducts: [],
    };
  },
  async created() {
    const mode = this.$route.params.mode || this.$route.query.mode;
    this.defaultGenre = this.defaultGenre.sort();

    console.log("Mode  ", this.$route);

    if (mode == "new") {
      // Check if we have analysis data from CreateListing
      const analysisData = this.$route.query.analysis;
      if (analysisData) {
        try {
          const analysis = JSON.parse(analysisData);
          console.log("Parsed analysis data:", analysis);

          // Pre-fill the form with analyzed data
          this.release = {
            ...this.release, // Keep default values
            ...analysis, // Override with Discogs data
          };

          // Normalize productType if it comes from AI
          if (this.release.productType) {
            const pt = this.release.productType.toLowerCase();
            if (pt.includes("vinyl")) this.release.productType = "vinyl";
            else if (pt.includes("cd")) this.release.productType = "cd";
            else if (pt.includes("cassette"))
              this.release.productType = "cassette";
          }

          // Ensure required fields have values
          if (!this.release.mood) {
            this.release.mood = this.moodOptions[0];
          }
          if (!this.release.genre) {
            this.release.genre = this.defaultGenre[0];
          }

          // Set weight based on product type
          this.handleCategory();
        } catch (error) {
          console.error("Error parsing analysis data:", error);
        }
      }
      // Add image from query to images array for preview if present
      const imageFromQuery = this.$route.query.image;
      if (
        imageFromQuery &&
        typeof imageFromQuery === "string" &&
        imageFromQuery.startsWith("data:image")
      ) {
        this.images.push(imageFromQuery);
      }
    } else if (mode == "edit") {
      const id = this.id;
      console.log("Edit");
      const response = await axios.get(this.ENDPOINT + "/listingsbysku/" + id);
      console.log("release data: ", response.data);
      this.release = response.data;
      this.handleCategory();
      // Ensure mood is set after loading from backend
      if (
        !this.release.mood ||
        this.release.mood === "" ||
        typeof this.release.mood === "undefined"
      ) {
        this.release.mood = this.moodOptions[0];
      }
    }

    // Getting purchase
    const resp = await axios.get(`${this.ENDPOINT}/purchases/ids`);
    console.log("purchase data: ", resp.data.purchases);
    this.purchases = resp.data.purchases;

    await this.fetchSimilarProducts();
  },
  methods: {
    getImageUrl(image) {
      if (!image) return "";
      if (image.startsWith("http://") || image.startsWith("https://"))
        return image;
      return `${this.ENDPOINT}/uploads/compressed/${image}`;
    },
    async handlePurchaseChange() {
      console.log("Selected Purchase ID:", this.purchase._id);
      // You can now use the selected ID in your logic
      const purchaseData = await axios.get(
        `${this.ENDPOINT}/purchases/${this.purchase._id}`,
      );
      console.log("purchaseData.data : ", purchaseData.data);
      if (purchaseData.data._id) {
        this.release.buyingPrice = Math.round(
          purchaseData.data.amount / purchaseData.data.totalItems,
        );
      }
    },
    addKeyword() {
      if (this.search && !this.release.keywords.includes(this.search)) {
        this.release.keywords.push(this.search);
      }
    },
    customFilter(item, queryText) {
      const text = item.toLowerCase();
      const query = queryText.toLowerCase();
      return text.indexOf(query) > -1;
    },

    handleFormatChange() {
      if (this.release.productType === "vinyl") {
        if (this.release.format === "78") {
          this.release.wcCategory = "Preowned 78 RPM Vinyl";
          this.release.speed = "78";
        } else {
          // Re-derive category from productType/condition/language
          this.handleCategory();
        }
      }
    },

    handleCategory() {
      if (this.release.productType != "") {
        switch (this.release.productType.toLowerCase()) {
          case "vinyl":
            // Set values for vinyl
            this.release.weight = "500";
            this.release.dimensions = {
              length: 35,
              width: 35,
              height: 5,
            };
            break;

          case "cd":
            // Set values for CD
            this.release.weight = "110";
            this.release.dimensions = {
              length: 12.5,
              width: 14.5,
              height: 1,
            };
            break;

          case "cassette":
            // Set values for cassette
            this.release.weight = "65";
            this.release.dimensions = {
              length: 10.2,
              width: 6.35,
              height: 1.27,
            };
            break;

          default:
            break;
        }
        const newData = this.categoryOptions.filter((category) => {
          const typeMatch = category
            .toLowerCase()
            .includes(this.release.productType.toLowerCase());
          const conditionMatch =
            this.release.condition.toLowerCase() === "used"
              ? category.toLowerCase().includes("preowned")
              : category
                  .toLowerCase()
                  .includes(this.release.condition.toLowerCase());
          let languageMatch;
          if (
            ["english", "hindi", "bengali"].includes(
              this.release.language.toLowerCase(),
            )
          ) {
            languageMatch = category
              .toLowerCase()
              .includes(this.release.language.toLowerCase());
          } else {
            languageMatch = category.toLowerCase().includes("other");
          }
          return typeMatch && conditionMatch && languageMatch;
        });
        console.log("Category: ", newData);
        this.release.wcCategory = newData[0];
        // Override category for 78 format regardless of language
        if (this.release.format === "78") {
          this.release.wcCategory = "Preowned 78 RPM Vinyl";
          this.release.speed = "78";
        }
        if (this.release.productType == "vinyl") {
          this.formatOptions = ["LP", "EP", "SP", "78"];
        } else if (this.release.productType == "cd") {
          this.formatOptions = ["Audio CD", "Video CD"];
        } else {
          this.formatOptions = ["Audio Cassette"];
        }
      }
    },

    async publish() {
      var c = window.confirm(
        "Have you updated the info? Are you sure you want to submit",
      );
      this.overlay = true;
      if (c) {
        try {
          const data = {
            customCategory: this.release.customCategory,
            status: "published",
            isPublished: true,
            published: true,
          };
          await axios.put(
            this.ENDPOINT + `/listings/${this.release._id}`,
            data,
          );
          await axios.post(
            this.ENDPOINT + "/listings/" + this.release._id + "/post",
          );
          this.overlay = false;
          this.showSnackbar("success", "Listing published successfully");
          this.$router.push("/home");
        } catch (e) {
          this.overlay = false;
          console.log(e);
          // alert("Error" + e.data.error)
        }
      }
    },

    async updateListing() {
      this.overlay = true;
      try {
        const response = await axios.put(
          this.ENDPOINT + `/listings/${this.release._id}`,
          this.release,
        );
        this.release = response.data;

        this.showSnackbar("success", "Listing updated successfully");
        this.overlay = false;
        const currentPath = this.$router.currentRoute.fullPath;

        // this.$router.push({ path: `/dummy${Date.now()}` }).then(() => {
        //     this.$router.replace(currentPath);
        // });
        // this.$router.go()
        window.location = currentPath;
      } catch (error) {
        this.overlay = false;
        this.showSnackbar(
          "error",
          "Failed to update listing: " + (error.data?.error || ""),
        );
      }
    },
    async deleteListing() {
      this.overlay = true;
      try {
        await axios.delete(this.ENDPOINT + `/listings/${this.release._id}`);
        this.showSnackbar("success", "Listing deleted successfully");
        this.overlay = false;
        this.$router.replace("/home"); // Redirect to home after delete
      } catch (error) {
        console.log(error);
        this.overlay = false;
        this.showSnackbar("error", "Failed to delete listing");
      }
    },

    removeImageForRelease(i) {
      console.log("removeImageForRelease called with index:", i);
      console.log(
        "Before remove - release.images array length:",
        this.release.images.length,
      );

      // Create a copy of the array
      const updatedImages = [...this.release.images];

      // Remove the element
      updatedImages.splice(i, 1);

      // Replace the original array
      this.release.images = updatedImages;

      console.log(
        "After remove - release.images array length:",
        this.release.images.length,
      );

      // Force re-computation of dependent properties
      this.$forceUpdate();
    },

    async save() {
      // console.log("array: ", this.release.customCategory);
      this.overlay = true;

      const self = this;

      const imageBlobs = await Promise.all(
        this.images.map(async (img, i) => {
          const blob = await self.dataURLtoBlob(img);
          function cleanString(inputStr) {
            return inputStr.replace(/[^a-zA-Z0-9]/g, "");
          }

          let timestamp = new Date().getTime(); // gets the current time in milliseconds
          let cleanedTitle = cleanString(self.release.title);
          let name = `${cleanedTitle}_image${i}_${timestamp}.png`;

          return { blob, name: name };
        }),
      );

      const formData = new FormData();
      imageBlobs.forEach(({ blob, name }) => {
        formData.append("images", blob, name);
      });

      // Stringify release object and append it to formData
      formData.append("release", JSON.stringify(this.release));

      try {
        const res = await axios.post(this.ENDPOINT + "/savelisting", formData);

        // handle server response...
        this.sku = res.data.sku; // assuming res.data.sku is the new SKU
        this.overlay = false;

        // Show success message with SweetAlert instead of snackbar
        this.$swal({
          title: "Success!",
          text: `SKU Generated Successfully: ${res.data.sku}`,
          icon: "success",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.replace("/home");
          }
        });
      } catch (e) {
        this.overlay = false;
        console.log(e);

        // Show error message with SweetAlert instead of snackbar
        this.$swal({
          title: "Error",
          text: e.data?.error || "Error generating SKU",
          icon: "error",
          confirmButtonText: "OK",
        });
      }

      this.overlay = false;
    },
    // ... your existing methods ...
    async onFileChange(e) {
      if (!e || this.isProcessingFiles) return;

      this.isProcessingFiles = true;

      try {
        // Reset pendingImages if it's a new upload
        this.pendingImages = [];

        // Handle multiple files
        const files = Array.isArray(e) ? e : [e];

        // Process each file and wait for all to be loaded
        const loadPromises = files.map((file) => {
          if (!file) return null;
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve(e.target.result);
            };
            reader.readAsDataURL(file);
          });
        });

        // Wait for all files to be loaded
        const loadedImages = await Promise.all(loadPromises);

        // Filter out any null values and add to pendingImages
        this.pendingImages = loadedImages.filter((img) => img !== null);

        // Show crop dialog for first image
        if (this.pendingImages.length > 0) {
          this.showNextCropDialog();
        }
      } finally {
        this.isProcessingFiles = false;
      }
    },

    showNextCropDialog() {
      if (!this.pendingImages || this.pendingImages.length === 0) {
        this.cropDialog = false;
        this.imageDataUrl = "";
        return;
      }
      this.imageDataUrl = this.pendingImages[0];
      this.cropperKey += 1;
      this.cropDialog = true;
    },

    cropImage() {
      if (!this.$refs.cropper) return;

      // Get the cropped result from the cropper
      const { canvas } = this.$refs.cropper.getResult({
        minWidth: 100,
        minHeight: 100,
        maxWidth: 1000,
        maxHeight: 1000,
      });

      if (!canvas) return;

      // Convert the canvas to a data URL
      const croppedImage = canvas.toDataURL("image/jpeg", 0.9);
      if (!this.images) this.images = [];
      this.images.push(croppedImage);

      // Remove the processed image from pendingImages
      if (this.pendingImages && this.pendingImages.length > 0) {
        this.pendingImages.shift();
      }

      // Show next image if available
      if (this.pendingImages && this.pendingImages.length > 0) {
        this.showNextCropDialog();
      } else {
        this.cropDialog = false;
        this.imageDataUrl = "";
      }
    },

    rotateImage() {
      if (this.$refs.cropper) {
        this.$refs.cropper.rotate(90);
      }
    },

    cancelCrop() {
      // Remove the current image from pending images
      if (this.pendingImages && this.pendingImages.length > 0) {
        this.pendingImages.shift();
      }

      // Show next image if available
      if (this.pendingImages && this.pendingImages.length > 0) {
        this.showNextCropDialog();
      } else {
        this.cropDialog = false;
        this.imageDataUrl = "";
      }
    },

    dataURLtoBlob(dataurl) {
      let arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    },
    addTrack() {
      this.release.tracklist.push({
        position: "",
        title: "",
        duration: "",
      });
    },
    removeTrack(item) {
      const index = this.release.tracklist.indexOf(item);
      if (index !== -1) {
        this.release.tracklist.splice(index, 1);
      }
    },
    // Helper method for showing snackbar notifications
    showSnackbar(color, text, timeout = 4000) {
      this.snackbarColor = color;
      this.snackbarText = text;
      this.snackbarTimeout = timeout;
      this.snackbar = true;
    },

    // Helper methods for status display
    getStatusText(release) {
      if (release.published === true) return "Published";
      if (release.isPublished) return "Published";
      if (release.status === "published") return "Published";
      return "Draft";
    },

    getStatusColor(release) {
      if (release.published === true) return "success";
      if (release.isPublished) return "success";
      if (release.status === "published") return "success";
      return "primary";
    },

    getStatusIcon(release) {
      if (release.published === true) return "mdi-check-circle";
      if (release.isPublished) return "mdi-check-circle";
      if (release.status === "published") return "mdi-check-circle";
      return "mdi-tag";
    },

    async generateGPT() {
      console.log(
        "this.release.productType.length",
        this.release.productType.length,
        this.release.productType,
      );
      if (this.release.productType.length == 0) {
        this.showSnackbar("warning", "Please choose product type");
        return;
      }
      this.overlay = true;

      console.log("Generate ", JSON.stringify(this.release));

      this.responseData = false;
      var genre = this.release.genre;

      const data = {
        title: this.release.title,
        artist: this.release.artist,
        year: this.release.year,
        label: this.release.label,
        // genre: genre,
        productType: this.release.productType,
        coverCondition: this.release.coverCondition,
        recordCondition: this.release.recordCondition,
        format: this.release.format,
        language: this.release.language,
        speed: this.release.speed,
        country: this.release.country,
        noofrec: this.release.noofrec,
        condition: this.release.condition,
      };

      try {
        const response = await axios.post(
          `${this.ENDPOINT}/generatedesc`,
          data,
        );

        this.release.title = this.release.customCategory.includes(
          "Japanese Pressings",
        )
          ? `${response.data.name} Japanese Pressing`
          : response.data.name;
        this.release.description = response.data.description;
        this.release.short_description = response.data.short_description;
        this.release.keywords = response.data.keywords;
        // Only update genre and mood if the backend returns a non-empty, non-placeholder value
        if (
          response.data.genre &&
          response.data.genre.trim() !== "" &&
          response.data.genre.trim().toLowerCase() !== "other" &&
          response.data.genre.trim().toLowerCase() !== "unknown" &&
          response.data.genre.trim() !== this.release.genre
        ) {
          this.release.genre = response.data.genre;
        }
        if (
          response.data.mood &&
          response.data.mood.trim() !== "" &&
          response.data.mood.trim().toLowerCase() !== "other" &&
          response.data.mood.trim().toLowerCase() !== "unknown" &&
          response.data.mood.trim() !== this.release.mood
        ) {
          this.release.mood = response.data.mood;
        }
        this.overlay = false;
      } catch (e) {
        this.showSnackbar("error", "Error generating description");
        this.overlay = false;
      }
      //Minify the images using tinify

      //Prepare the info for wordpress

      //Submit to wordpress
    },
    addKeyword(keyword) {
      if (keyword && !this.release.keywords.includes(keyword)) {
        this.release.keywords.push(keyword);
      }
    },

    async fetchSimilarProducts() {
      console.log("fetchSimilarProducts entered");
      if (!this.release.title) return;
      try {
        const resp = await axios.get(`${this.ENDPOINT}/listings`, {
          params: {},
        });
        // Improved substring or token-inclusion similarity
        function isSimilarTitle(a, b) {
          a = a.toLowerCase().trim();
          b = b.toLowerCase().trim();
          if (a.length === 0 || b.length === 0) return false;
          // Substring match
          if (a.includes(b) || b.includes(a)) return true;
          // Token inclusion
          const tokensA = a.split(/\W+/).filter(Boolean);
          const tokensB = b.split(/\W+/).filter(Boolean);
          const shorter = tokensA.length < tokensB.length ? tokensA : tokensB;
          const longer = tokensA.length < tokensB.length ? tokensB : tokensA;
          return shorter.every((token) => longer.includes(token));
        }
        this.similarProducts = resp.data.listings.filter(
          (p) =>
            p.sku !== this.release.sku &&
            isSimilarTitle(p.title, this.release.title),
        );
        console.log("similarProducts", this.similarProducts);
      } catch (e) {
        console.error("Error fetching similar products:", e);
      }
    },
    moveImageUp(index) {
      console.log("moveImageUp called with index:", index);
      console.log("Before swap - images array length:", this.images.length);
      if (index > 0) {
        // Create a copy of the array
        const updatedImages = [...this.images];

        // Swap elements
        const temp = updatedImages[index];
        updatedImages[index] = updatedImages[index - 1];
        updatedImages[index - 1] = temp;

        // Replace the original array
        this.images = updatedImages;

        console.log("After swap - images array length:", this.images.length);

        // Force re-computation of dependent properties
        this.$forceUpdate();
      }
    },
    moveImageDown(index) {
      console.log("moveImageDown called with index:", index);
      console.log("Before swap - images array length:", this.images.length);
      if (index < this.images.length - 1) {
        // Create a copy of the array
        const updatedImages = [...this.images];

        // Swap elements
        const temp = updatedImages[index];
        updatedImages[index] = updatedImages[index + 1];
        updatedImages[index + 1] = temp;

        // Replace the original array
        this.images = updatedImages;

        console.log("After swap - images array length:", this.images.length);

        // Force re-computation of dependent properties
        this.$forceUpdate();
      }
    },
    moveExistingImageUp(index) {
      console.log("moveExistingImageUp called with index:", index);
      console.log(
        "Before swap - release.images array length:",
        this.release.images.length,
      );
      if (index > 0) {
        // Create a copy of the array
        const updatedImages = [...this.release.images];

        // Swap elements
        const temp = updatedImages[index];
        updatedImages[index] = updatedImages[index - 1];
        updatedImages[index - 1] = temp;

        // Replace the original array
        this.release.images = updatedImages;

        console.log(
          "After swap - release.images array length:",
          this.release.images.length,
        );

        // Force re-computation of dependent properties
        this.$forceUpdate();
      }
    },
    moveExistingImageDown(index) {
      console.log("moveExistingImageDown called with index:", index);
      console.log(
        "Before swap - release.images array length:",
        this.release.images.length,
      );
      if (index < this.release.images.length - 1) {
        // Create a copy of the array
        const updatedImages = [...this.release.images];

        // Swap elements
        const temp = updatedImages[index];
        updatedImages[index] = updatedImages[index + 1];
        updatedImages[index + 1] = temp;

        // Replace the original array
        this.release.images = updatedImages;

        console.log(
          "After swap - release.images array length:",
          this.release.images.length,
        );

        // Force re-computation of dependent properties
        this.$forceUpdate();
      }
    },
    removeImage(i) {
      console.log("removeImage called with index:", i);
      console.log("Before remove - images array length:", this.images.length);

      // Create a copy of the array
      const updatedImages = [...this.images];

      // Remove the element
      updatedImages.splice(i, 1);

      // Replace the original array
      this.images = updatedImages;

      console.log("After remove - images array length:", this.images.length);

      // Force re-computation of dependent properties
      this.$forceUpdate();
    },
  },
};
</script>

<style>
/* Global fixes to prevent overflow */
.v-application {
  max-width: 100vw !important;
  overflow-x: hidden !important;
}

.v-data-table {
  max-width: 100% !important;
  overflow-x: auto !important;
}

.v-data-table__wrapper {
  max-width: 100% !important;
  overflow-x: auto !important;
}

.v-card {
  max-width: 100% !important;
  word-break: break-word !important;
}

.release-header {
  margin-bottom: 16px;
}

.album-cover {
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.edit-mode-container {
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.overflow {
  overflow: hidden;
}

/* Ensure the vue-cropper is constrained to its container */
.vue-cropper {
  max-width: 100%;
  max-height: 400px;
}

.step-title {
  width: 100%;
  display: flex;
  align-items: center;
}

.release-container-fix {
  max-width: 1400px;
}
</style>

<style scoped>
.release-wrapper {
  max-width: 100vw;
  overflow-x: hidden;
  width: 100%;
  position: relative;
  box-sizing: border-box;
}

.release-container {
  background-color: #f8f9fa;
}

.release-container-fix {
  max-width: 100% !important;
  padding-left: 1rem !important;
  padding-right: 1rem !important;
  overflow-x: hidden !important;
  box-sizing: border-box !important;
}

.release-header {
  border-radius: 12px !important;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

.album-cover {
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.album-card {
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

.market-info-card {
  border-radius: 8px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.form-section-card {
  border-radius: 8px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  transition: box-shadow 0.3s ease;
}

.form-section-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Form field styling */
.v-text-field.rounded-lg >>> .v-input__slot,
.v-select.rounded-lg >>> .v-input__slot {
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.v-text-field.rounded-lg >>> .v-input__slot:hover,
.v-select.rounded-lg >>> .v-input__slot:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
}

/* Tracklist table styling */
.tracklist-table {
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.tracklist-table >>> .v-data-table__wrapper {
  border-radius: 8px !important;
}

/* Action buttons styling */
.action-buttons {
  padding: 16px !important;
  background-color: #f8f9fa;
  border-radius: 8px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile responsiveness fixes */
@media screen and (max-width: 960px) {
  /* Container and layout fixes */
  .v-container {
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    overflow-x: hidden !important;
  }

  .v-row {
    margin: 0 !important;
    width: 100% !important;
  }

  .v-col {
    padding: 4px !important;
    flex-basis: 100% !important;
    max-width: 100% !important;
  }

  /* Card fixes */
  .v-card {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .release-header {
    border-radius: 0 !important;
    margin-bottom: 12px;
    width: 100% !important;
  }

  .v-card-text {
    padding: 8px !important;
  }

  /* Table fixes */
  .v-data-table {
    width: 100% !important;
    overflow-x: auto !important;
  }

  .v-data-table__wrapper {
    overflow-x: auto !important;
  }

  /* Form field fixes */
  .v-text-field,
  .v-select {
    width: 100% !important;
    max-width: 100% !important;
  }

  .v-input__slot {
    min-height: 40px !important;
    max-width: 100% !important;
  }

  /* Typography fixes */
  .text-h5 {
    font-size: 1.1rem !important;
    line-height: 1.3 !important;
  }

  .text-subtitle-1 {
    font-size: 0.875rem !important;
  }

  /* Image gallery fixes */
  .image-gallery {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100% !important;
  }

  /* Button fixes */
  .v-btn {
    margin: 4px !important;
    padding: 0 8px !important;
  }

  /* Chip fixes */
  .v-chip {
    margin: 2px !important;
  }
}

/* Small mobile devices */
@media screen and (max-width: 480px) {
  .v-card-title {
    padding: 8px !important;
    font-size: 16px !important;
  }

  .v-card-text {
    padding: 6px !important;
  }

  .v-col {
    padding: 2px !important;
  }

  .release-container-fix {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  /* Make form fields stack vertically */
  .v-input {
    margin-bottom: 8px !important;
  }

  /* Reduce image sizes */
  .album-cover {
    height: 200px !important;
  }

  /* Fix overflow issues */
  .v-data-table {
    font-size: 12px !important;
  }

  /* Fix button sizes */
  .v-btn {
    min-width: auto !important;
    padding: 0 6px !important;
  }
}

/* Track position styling */
.track-position {
  font-weight: 500;
  color: #555;
}

/* Image gallery styling */
.image-card {
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Section dividers */
.section-divider {
  margin: 24px 0;
  opacity: 0.6;
}

/* Fade transitions for smoother UI */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

/* Button styling */
.action-btn {
  border-radius: 8px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  transition: box-shadow 0.3s ease;
}

.action-btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Add these styles at the end of your existing styles */
.cropper-container {
  height: 400px;
  width: 100%;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.cropper-container .vue-cropper {
  height: 100%;
  width: 100%;
}

/* Ensure the cropper maintains aspect ratio */
.vue-cropper {
  background-color: #f5f5f5;
}

/* Style the cropper view */
.vue-cropper .cropper-view-box,
.vue-cropper .cropper-face {
  border-radius: 0;
}

/* Ensure the dialog content is properly sized */
.v-dialog {
  max-height: 90vh;
  overflow-y: auto;
}

/* Add the styles for the simple cropper */
.v-dialog.overflow {
  overflow: visible;
}

.vue-cropper {
  height: 300px;
  width: 100%;
}

/* Add specific styles for the crop dialog */
.crop-dialog {
  z-index: 1000 !important;
}

.cropper-wrapper {
  overflow: hidden;
  background: #f0f0f0;
  border-radius: 4px;
  position: relative;
}

.image-order-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: var(--v-primary-base);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.v-card.mx-auto {
  position: relative;
  transition: transform 0.2s;
  border-radius: 8px;
  overflow: hidden;
}

.v-card.mx-auto:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.v-card-actions {
  background-color: rgba(0, 0, 0, 0.03);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

/* Mobile improvements */
.image-gallery-item {
  padding: 6px;
}

.image-card {
  max-width: 100%;
  margin: 0 auto;
}

.image-card-actions {
  padding: 8px 4px !important;
  flex-wrap: wrap;
  justify-content: center;
}

.image-card-actions .v-btn {
  margin: 2px;
}

/* Override v-card image container for mobile */
.v-image__image {
  background-size: contain !important;
}

/* Mobile responsive fixes */
@media screen and (max-width: 600px) {
  .image-gallery-item {
    padding: 4px;
  }

  .image-card-actions {
    padding: 4px 2px !important;
  }

  .image-card-actions .v-btn {
    margin: 1px;
    min-width: auto !important;
  }

  .v-btn--outlined {
    padding: 0 4px !important;
  }

  .image-order-badge {
    top: 4px;
    right: 4px;
    padding: 1px 6px;
    font-size: 0.7rem;
  }
}

.v-stepper {
  margin-bottom: 4rem;
}

/* Add styles for vue-advanced-cropper */
.mobile-cropper {
  height: 70vh;
  max-height: 400px;
}

.vue-advanced-cropper {
  background-color: #f5f5f5;
  max-width: 100%;
  margin: 0 auto;
  height: 400px;
}

/* Fix for stencil visibility */
.vue-advanced-cropper__stretcher {
  z-index: 100;
}

.vue-advanced-cropper__boundary {
  background: rgba(0, 0, 0, 0.4);
}

/* Improve handle styling for mobile */
.vue-advanced-cropper .vue-handler-wrapper .vue-handler {
  width: 24px !important;
  height: 24px !important;
  background: rgba(255, 255, 255, 0.85) !important;
  border: 2px solid #1976d2 !important;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2) !important;
}

/* Mobile-specific cropper styles */
@media screen and (max-width: 600px) {
  .vue-advanced-cropper {
    height: 60vh;
    max-height: 300px;
  }

  .v-dialog {
    margin: 8px;
    max-width: calc(100% - 16px) !important;
  }

  /* Make handles even larger on small screens */
  .vue-advanced-cropper .vue-handler-wrapper .vue-handler {
    width: 30px !important;
    height: 30px !important;
  }
}
</style>
